import { getCountryByTwoLetterCode, isEmptyOrUndefined, removeStringWhiteSpaces } from "@/utils/utils";
import { AutoComplete, Spin } from "antd";
import { DefaultOptionType } from "antd/es/select";
import debounce from "lodash/debounce";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGoogleMapsApi } from "../../../app/contexts/GoogleMapsApiContext";
import { DEFAULT_LOCALE } from "../../../app/i18n";
import { BratislavaCityDistricts, KosiceCityDistricts } from "../../constants";
import { Address } from "../../types";
import AntIcon from "../icons/AntIcon";

const MIN_SEARCH_VALUE_LENGTH = 4;

interface Props {
  hide?: boolean;
  onAddressFind: (address: Address) => void;
}

export const AddressAutocomplete = ({ hide, onAddressFind }: Props) => {
  const { t } = useTranslation();

  const { googleApi } = useGoogleMapsApi();
  const [value, setValue] = useState<string>();
  const [options, setOptions] = useState<DefaultOptionType[]>([]);
  const [inProgress, setInProgress] = useState<boolean>(false);
  // Each completed or abandoned search gets a new Google session, which bumps the generation.
  const [sessionGeneration, setSessionGeneration] = useState(0);

  const sessionToken = useMemo(
    () => (googleApi ? new googleApi.AutocompleteSessionToken() : undefined),
    [googleApi, sessionGeneration],
  );

  const handleSearchDebounced = useMemo(
    () =>
      debounce((value: string) => {
        if (!googleApi) {
          console.error("Google Maps API is not initialized.");
          return;
        }

        if (value.length < MIN_SEARCH_VALUE_LENGTH) {
          return;
        }

        setInProgress(true);

        googleApi.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: value,
          sessionToken,
          language: DEFAULT_LOCALE,
          region: DEFAULT_LOCALE,
        })
          .then(({ suggestions }) => {
            setOptions(
              suggestions
                .filter((suggestion) => !!suggestion.placePrediction)
                .map<DefaultOptionType>((suggestion) => ({
                  label: suggestion.placePrediction?.text?.text,
                  value: suggestion.placePrediction?.placeId,
                })),
            );
          })
          .finally(() => {
            setInProgress(false);
          });
      }, 500),
    [sessionToken],
  );

  const handleSearch = useCallback(
    (value: string) => {
      handleSearchDebounced(value);
    },
    [handleSearchDebounced],
  );

  const handleSelect = (value: string) => {
    if (!googleApi) {
      console.error("Google Maps API is not initialized.");
      return;
    }

    const googleApiPlace = new googleApi.Place({
      id: value,
      requestedLanguage: DEFAULT_LOCALE,
      requestedRegion: DEFAULT_LOCALE,
    });

    googleApiPlace
      .fetchFields({
        fields: ["addressComponents"],
      })
      .then(({ place }) => {
        const address: Partial<Address> = {
          street: undefined,
          descriptiveNumber: undefined,
          orientationNumber: undefined,
          cityDistrict: undefined,
          city: undefined,
          zipCode: undefined,
          country: undefined,
        };

        place.addressComponents?.forEach((component) => {
          switch (component.types[0]) {
            case "route":
              address.street = component.longText ?? undefined;
              break;
            case "premise":
              address.descriptiveNumber = component.longText ?? undefined;
              break;
            case "street_number":
              address.orientationNumber = component.shortText ?? undefined;
              break;
            case "sublocality_level_1":
              address.cityDistrict = component.longText ?? undefined;
              break;
            case "locality":
              address.city = component.longText ?? undefined;
              break;
            case "postal_code":
              address.zipCode = removeStringWhiteSpaces(component.longText ?? undefined);
              break;
            case "country":
              address.country = component.shortText ? getCountryByTwoLetterCode(component.shortText) : undefined;
              break;
          }
        });

        if (address.cityDistrict) {
          if (!address.city) {
            address.city = address.cityDistrict;
            address.cityDistrict = undefined;
          } else if (!(
            (address.city === "Bratislava" && BratislavaCityDistricts.includes(address.cityDistrict)) ||
            (address.city === "Košice" && KosiceCityDistricts.includes(address.cityDistrict))
          )) {
            address.cityDistrict = undefined;
          }
        }

        onAddressFind(address as Address);
      })
      .catch(() => console.error("Error occurred while executing place details request."))
      .finally(() => {
        handleClear();
      });
  };

  const handleClear = () => {
    setValue(undefined);
    setOptions([]);
    setInProgress(false);
    setSessionGeneration((generation) => generation + 1);
  };

  return googleApi && !hide ? (
    <>
      <Spin spinning={inProgress} style={{ marginTop: "-3px" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <AutoComplete
            style={{ width: 230 }}
            popupMatchSelectWidth={false}
            value={value}
            options={options}
            onChange={(value, option) => {
              if (!isEmptyOrUndefined(option)) {
                setValue(undefined);
              } else {
                setValue(value);
              }
            }}
            showSearch={{ onSearch: handleSearch }}
            onSelect={handleSelect}
            onBlur={handleClear}
            onClear={handleClear}
            notFoundContent={
              (value || "").length >= MIN_SEARCH_VALUE_LENGTH && (
                <div className="sub-header-info center-align">{t("address.search.noResult")}</div>
              )
            }
            placeholder={
              <>
                <AntIcon type="search" />
                {t("address.search.placeholder")}
              </>
            }
          />
        </div>
      </Spin>
    </>
  ) : null;
};
